/**
 * Channel state observer with re-attach backoff for `private:user.${id}`.
 *
 * Mirrors `APL-A.2` / `AND-A.2`. Ably-JS auto-recovers `suspended` channels
 * (channelRetryTimeout default 15s) but `failed` and `detached` go silent.
 * This helper listens on the raw Ably channel (Echo doesn't surface channel
 * state events) and re-`attach()`s with backoff [2s, 5s, 15s], cap 3 attempts.
 *
 * Reach into `(echo as any).connector.ably.channels.get(name)` — the same
 * private-API pattern used in `connection.store.ts` for the connection-state
 * listener and the rewind pre-warm. If Echo's internals ever shift and the
 * cast yields `undefined`, the helper degrades to a no-op (we lose retry
 * coverage but don't break the connection).
 *
 * See docs/REALTIME_RECONCILE_PLAN.md → WEB-A.2.
 */

import { wsLogger } from '@/shared/lib/logger';
import type { TypedEchoInstance } from './types/echo.types';

const REATTACH_DELAYS_MS = [2_000, 5_000, 15_000] as const;
const MAX_REATTACH_ATTEMPTS = REATTACH_DELAYS_MS.length;

interface AblyChannelStateChange {
  current: string;
  previous: string;
  reason?: { message?: string };
}

type AblyChannelListener = (stateChange: AblyChannelStateChange) => void;

interface AblyChannelLike {
  on: (eventOrEvents: string | string[], callback: AblyChannelListener) => void;
  off: (
    eventOrEvents?: string | string[],
    callback?: AblyChannelListener
  ) => void;
  attach: (callback?: (err?: unknown) => void) => void;
}

let activeTeardown: (() => void) | null = null;

function getRawChannel(
  echo: TypedEchoInstance,
  channelName: string
): AblyChannelLike | null {
  try {
    const channels = (
      echo as unknown as {
        connector?: {
          ably?: {
            channels?: { get: (name: string) => AblyChannelLike };
          };
        };
      }
    )?.connector?.ably?.channels;
    if (!channels) return null;
    return channels.get(channelName);
  } catch (error) {
    wsLogger.warn('Failed to access raw Ably channel for state observer', {
      channelName,
      error: error instanceof Error ? error.message : String(error),
    });
    return null;
  }
}

export function setupChannelReattach(
  echo: TypedEchoInstance,
  userId: string,
  onExhausted: (reason: string) => void
): () => void {
  // Idempotent re-setup — tear down any previous wiring (credential swap,
  // re-mount, etc.) before installing a new listener pair.
  if (activeTeardown) {
    activeTeardown();
  }

  const channelName = `private:user.${userId}`;
  const channel = getRawChannel(echo, channelName);
  if (!channel) {
    activeTeardown = null;
    return () => {};
  }

  let attempts = 0;
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const onAttached: AblyChannelListener = () => {
    if (attempts > 0) {
      wsLogger.info('[ws-channel] attached — resetting reattach counter', {
        previousAttempts: attempts,
      });
    }
    attempts = 0;
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
  };

  const onFailedOrDetached: AblyChannelListener = (stateChange) => {
    const reason = stateChange.reason?.message || stateChange.current;

    if (attempts >= MAX_REATTACH_ATTEMPTS) {
      wsLogger.error('[ws-channel] reattach attempts exhausted', {
        reason,
        attempts,
      });
      onExhausted(reason);
      return;
    }

    if (timeoutId) clearTimeout(timeoutId);
    const delay = REATTACH_DELAYS_MS[attempts];
    attempts += 1;

    wsLogger.warn(
      `[ws-channel] ${stateChange.current} — scheduling reattach #${attempts}/${MAX_REATTACH_ATTEMPTS}`,
      { reason, delayMs: delay }
    );

    timeoutId = setTimeout(() => {
      timeoutId = null;
      try {
        channel.attach();
      } catch (error) {
        wsLogger.warn('[ws-channel] manual attach call threw', {
          error: error instanceof Error ? error.message : String(error),
        });
      }
    }, delay);
  };

  try {
    channel.on('attached', onAttached);
    channel.on(['failed', 'detached'], onFailedOrDetached);
  } catch (error) {
    wsLogger.warn('Failed to attach channel state listeners', {
      error: error instanceof Error ? error.message : String(error),
    });
    activeTeardown = null;
    return () => {};
  }

  const teardown = () => {
    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }
    try {
      channel.off('attached', onAttached);
      channel.off(['failed', 'detached'], onFailedOrDetached);
    } catch {
      // Best-effort cleanup — Ably channel may already be released.
    }
    attempts = 0;
  };

  activeTeardown = teardown;
  return teardown;
}

export function teardownChannelReattach(): void {
  if (activeTeardown) {
    activeTeardown();
    activeTeardown = null;
  }
}
